/**
 * ContactJourneyDrawer (PR-B)
 *
 * Slide-from-right drawer with 2 tabs:
 *   - "Jornada" (graph) — heavy, only mounts when its tab is active
 *   - "Logs" (card list) — lightweight, default tab
 *
 * Mounting strategy: we *unmount* the inactive tab so the journey graph
 * doesn't pay for ResizeObserver and DOM nodes when the user is reading the
 * log list. This isolates the latency the user explicitly worried about.
 */
import React, { useState } from 'react';
import { X, GitBranch, ListChecks } from 'lucide-react';
import ContactJourneyGraph from './ContactJourneyGraph';
import ContactJourneyLogs from './ContactJourneyLogs';

const ContactJourneyDrawer = ({ isOpen, onClose, contact, colors }) => {
  const [tab, setTab] = useState('logs'); // 'logs' is the cheaper default

  if (!isOpen || !contact) return null;

  // Chave da jornada: telefone no WhatsApp, chave opaca no Instagram e no
  // Messenger (o cartao desses canais vem com phone nulo e contact_ref).
  const journeyKey = contact.contact_ref || contact.phone || '';
  const display = contact.display_name || contact.phone || journeyKey;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000,
      display: 'flex', justifyContent: 'flex-end',
    }}>
      <div style={{
        width: 640, maxWidth: '100%', height: '100%',
        backgroundColor: colors.bgPrimary,
        boxShadow: '-8px 0 32px rgba(0,0,0,0.25)',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 20px', borderBottom: `1px solid ${colors.borderColor}`,
        }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <h3 style={{
              margin: 0, fontSize: 16, fontWeight: 600, color: colors.textPrimary,
            }}>
              Jornada do contato
            </h3>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: colors.textSecondary }}>
              {display}{contact.phone ? ` · +${contact.phone}` : ''}
            </p>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: colors.textSecondary, padding: 8, borderRadius: 8,
          }}>
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex', gap: 4, padding: '8px 20px',
          borderBottom: `1px solid ${colors.borderColor}`,
        }}>
          {[
            { value: 'logs', label: 'Logs', Icon: ListChecks },
            { value: 'journey', label: 'Jornada', Icon: GitBranch },
          ].map(t => {
            const isActive = tab === t.value;
            const Icon = t.Icon;
            return (
              <button
                key={t.value}
                onClick={() => setTab(t.value)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '6px 12px', borderRadius: 8, fontSize: 13,
                  fontWeight: 500, cursor: 'pointer', border: 'none',
                  backgroundColor: isActive ? colors.accentPrimary : 'transparent',
                  color: isActive ? '#fff' : colors.textSecondary,
                  transition: 'all 0.15s',
                }}
              >
                <Icon size={13} />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Body */}
        <div style={{ flex: 1, padding: 20, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {tab === 'logs' && (
            <ContactJourneyLogs phone={journeyKey} colors={colors} />
          )}
          {tab === 'journey' && (
            <ContactJourneyGraph phone={journeyKey} colors={colors} />
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactJourneyDrawer;
